/* ==========================================================================
   JP Feature Highlights
   Carousel with autoplay, pause, resume, hover pause, and multi-instance support
   ========================================================================== */

(() => {
  'use strict';

  const SELECTOR = '[data-jp-feature-highlights]';
  const INACTIVITY_DELAY = 5000;

  class JPFeatureHighlights {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-jp-feature-highlights-viewport]');
      this.items = Array.from(root.querySelectorAll('.jp-feature-highlights__item'));

      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.pauseOnHover = root.dataset.pauseHover === 'true';
      this.delay = Number(root.dataset.delay) || 4000;

      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.currentIndex = 0;
      this.isUserPaused = false;
      this.isHoverPaused = false;

      if (!this.viewport || this.items.length <= 1) {
        return;
      }

      this.bindEvents();

      if (this.autoplayEnabled && !document.hidden) {
        this.startAutoplay();
      }

      this.root.dataset.jpFeatureHighlightsInitialized = 'true';
    }

    bindEvents() {
      this.onInteraction = this.handleUserInteraction.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onMouseEnter = this.handleMouseEnter.bind(this);
      this.onMouseLeave = this.handleMouseLeave.bind(this);

      this.viewport.addEventListener('pointerdown', this.onInteraction, { passive: true });
      this.viewport.addEventListener('touchstart', this.onInteraction, { passive: true });
      this.viewport.addEventListener('wheel', this.onInteraction, { passive: true });
      this.viewport.addEventListener('keydown', this.onInteraction);
      this.viewport.addEventListener('scroll', this.updateCurrentIndex.bind(this), { passive: true });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter);
        this.root.addEventListener('mouseleave', this.onMouseLeave);
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onResize);
    }

    handleResize() {
      this.scrollToIndex(this.currentIndex, false);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        this.clearResumeTimer();
        return;
      }

      if (this.autoplayEnabled && !this.isUserPaused && !this.isHoverPaused) {
        this.startAutoplay();
      }
    }

    handleMouseEnter() {
      if (!this.autoplayEnabled || !this.pauseOnHover) {
        return;
      }

      this.isHoverPaused = true;
      this.stopAutoplay();
    }

    handleMouseLeave() {
      if (!this.autoplayEnabled || !this.pauseOnHover) {
        return;
      }

      this.isHoverPaused = false;

      if (!document.hidden && !this.isUserPaused) {
        this.startAutoplay();
      }
    }

    handleUserInteraction() {
      if (!this.autoplayEnabled) {
        return;
      }

      this.isUserPaused = true;
      this.stopAutoplay();
      this.clearResumeTimer();

      this.resumeTimer = window.setTimeout(() => {
        this.isUserPaused = false;

        if (!document.hidden && !this.isHoverPaused) {
          this.startAutoplay();
        }
      }, INACTIVITY_DELAY);
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.autoplayTimer ||
        document.hidden ||
        this.isUserPaused ||
        this.isHoverPaused
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.goToNext();
      }, this.delay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    clearResumeTimer() {
      if (!this.resumeTimer) {
        return;
      }

      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }

    goToNext() {
      const nextIndex = this.currentIndex + 1 >= this.items.length ? 0 : this.currentIndex + 1;
      this.scrollToIndex(nextIndex, true);
    }

    scrollToIndex(index, smooth = true) {
      const target = this.items[index];

      if (!target) {
        return;
      }

      this.viewport.scrollTo({
        left: target.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto'
      });

      this.currentIndex = index;
    }

    updateCurrentIndex() {
      const viewportLeft = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - viewportLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.currentIndex = closestIndex;
    }
  }

  const initFeatureHighlights = () => {
    const roots = document.querySelectorAll(SELECTOR);

    roots.forEach((root) => {
      if (root.dataset.jpFeatureHighlightsInitialized === 'true') {
        return;
      }

      new JPFeatureHighlights(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFeatureHighlights);
  } else {
    initFeatureHighlights();
  }

  document.addEventListener('shopify:section:load', initFeatureHighlights);
  document.addEventListener('shopify:block:select', initFeatureHighlights);
})();